// AudioBubble.jsx
// Place in src/components/AudioBubble.jsx
//
// WhatsApp-style voice note player.
// Extracted from AgentDashboard.jsx so it can be shared with AgentFocusModal.
//
// Usage:
//   import AudioBubble from './AudioBubble';
//   <AudioBubble audioUrl={msg.audioUrl} isUser={msg.role === 'user'} autoPlay={false} />

import React from 'react';

const AudioBubble = ({ audioUrl, isUser, autoPlay, onAutoPlayed }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [hasAutoPlayed, setHasAutoPlayed] = React.useState(false);

React.useEffect(() => {
  if (autoPlay && !hasAutoPlayed && audioRef.current && audioUrl) {
    audioRef.current.play()
      .then(() => {
        setHasAutoPlayed(true);
        if (onAutoPlayed) onAutoPlayed();
      })
      .catch(e => {
        console.warn("Autoplay blocked:", e.message);
        setHasAutoPlayed(true);
        // Still consume the flag — user can click play manually
        if (onAutoPlayed) onAutoPlayed();
      });
  }
}, [audioUrl, autoPlay, hasAutoPlayed, onAutoPlayed]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(e => console.warn("Play failed:", e));
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const fg   = isUser ? "#ffffff" : "#1a6fba";
  const bg   = isUser ? "rgba(255,255,255,0.25)" : "rgba(26,111,186,0.18)";
  const fill = isUser ? "#ffffff" : "#1a6fba";

  if (!audioUrl) {
    return (
      <div style={{ fontSize: 12, color: fg, fontStyle: "italic" }}>
        🎙 Voice note (loading…)
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 180 }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        preload="metadata"
      />
      <button
        onClick={togglePlay}
        style={{
          width: 32, height: 32, borderRadius: "50%",
          backgroundColor: fg, color: isUser ? "#1a6fba" : "#ffffff",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, flexShrink: 0,
        }}
        title={playing ? "Pause" : "Play"}
      >
        {playing ? "⏸" : "▶"}
      </button>
      <div
        onClick={seek}
        style={{
          flex: 1, height: 4, backgroundColor: bg, borderRadius: 2,
          cursor: "pointer", position: "relative", minWidth: 80,
        }}
      >
        <div style={{
          width: duration ? `${(currentTime / duration) * 100}%` : "0%",
          height: "100%", backgroundColor: fill, borderRadius: 2,
        }} />
      </div>
      <span style={{ fontSize: 11, color: fg, minWidth: 35, fontVariantNumeric: "tabular-nums" }}>
        {fmt(playing || currentTime > 0 ? currentTime : duration)}
      </span>
    </div>
  );
};

export default AudioBubble;
